import { useState } from "react"

// Field definitions per mode. Order here drives the rendered order + stagger.
export const FIELDS = {
  login: [
    { name: "email", label: "Пошта", type: "email", autoComplete: "email" },
    { name: "password", label: "Пароль", type: "password", autoComplete: "current-password" },
  ],
  signup: [
    { name: "name", label: "Імʼя", type: "text", autoComplete: "name" },
    { name: "email", label: "Пошта", type: "email", autoComplete: "email" },
    { name: "password", label: "Пароль", type: "password", autoComplete: "new-password" },
    { name: "password_confirmation", label: "Повтори пароль", type: "password", autoComplete: "new-password" },
  ],
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Owns the auth form's values, client validation and the fetch submission
// lifecycle (idle → submitting → success), mirroring useBookForm.
const useAuthForm = (config) => {
  const fields = FIELDS[config.mode]
  const blank = Object.fromEntries(fields.map((f) => [f.name, ""]))

  const [values, setValues] = useState(blank)
  const [errors, setErrors] = useState({})
  const [status, setStatus] = useState("idle") // idle | submitting | success
  const [formError, setFormError] = useState(null)
  const [shake, setShake] = useState(false)

  const triggerShake = () => {
    setShake(true)
    setTimeout(() => setShake(false), 500)
  }

  const change = (name, value) => {
    setValues((v) => ({ ...v, [name]: value }))
    if (errors[name]) setErrors((e) => ({ ...e, [name]: null }))
    if (formError) setFormError(null)
  }

  const validate = () => {
    const next = {}
    fields.forEach(({ name }) => {
      if (!String(values[name]).trim()) next[name] = "Заповни це поле"
    })
    if (!next.email && values.email && !EMAIL_RE.test(values.email.trim())) {
      next.email = "Пошта виглядає некоректно"
    }
    if (!next.password && values.password && values.password.length < 6) {
      next.password = "Мінімум 6 символів"
    }
    if (config.mode === "signup" && !next.password_confirmation &&
        values.password && values.password !== values.password_confirmation) {
      next.password_confirmation = "Паролі не співпадають"
    }
    return next
  }

  const submit = async (e) => {
    e.preventDefault()
    if (status !== "idle") return

    const found = validate()
    if (Object.keys(found).length) {
      setErrors(found)
      triggerShake()
      return
    }

    setStatus("submitting")
    setFormError(null)

    const payload = {}
    fields.forEach(({ name }) => { payload[name] = values[name] })

    try {
      const res = await fetch(config.action, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": config.csrf,
          Accept: "application/json",
        },
        body: JSON.stringify({ [config.param]: payload }),
      })
      const data = await res.json().catch(() => ({}))

      if (res.ok && data.ok) {
        setStatus("success")
        setTimeout(() => {
          if (window.Turbo) window.Turbo.visit(data.redirect)
          else window.location.href = data.redirect
        }, 650)
      } else {
        const serverErrors = data.errors || {}
        const mapped = {}
        Object.entries(serverErrors).forEach(([key, msgs]) => {
          const msg = Array.isArray(msgs) ? msgs[0] : String(msgs)
          if (key === "base") setFormError(msg)
          else mapped[key] = msg
        })
        // A bare email-uniqueness clash etc. with no field map still needs feedback.
        if (!Object.keys(mapped).length && !serverErrors.base) {
          setFormError("Не вдалося. Перевір введені дані.")
        }
        setErrors(mapped)
        setStatus("idle")
        triggerShake()
      }
    } catch (err) {
      setFormError("Не вдалося зʼєднатися. Спробуй ще раз.")
      setStatus("idle")
    }
  }

  return { fields, values, errors, status, formError, shake, change, submit }
}

export default useAuthForm
